import type { ReactNode } from "react";
import { getSegments } from "sql-highlight";

/**
 * The app's one code highlighter (owner feedback 2026-09-30: the Query dialog's SQL "as proper
 * sql with text highlights"; since 2026-10-14 the source SQL only). skypoint-ui's `SkyCodeBlock` draws the frame,
 * the scrolling and the copy text, and adds no parser; it takes `highlight(code, language)`, which is
 * `highlightCode` here. Only SQL is known: any other language answers `undefined`, and the block
 * shows its text plain.
 *
 * The tokenizer is `sql-highlight`'s (MIT, no dependencies): it splits the text into keyword,
 * function, number, string, special (operators and punctuation), bracket, comment, identifier and
 * whitespace segments, and the segments join back to the text exactly. Its keyword list is generic
 * SQL, so the words dbt's Snowflake and Databricks SQL use that it does not know (`transient`,
 * `qualify`, `over`, `partition`, `merge` ...) are read as keywords here too.
 *
 * Every colour is a semantic token (`scripts/check-palette.mjs`), so it follows the light and dark
 * themes; each keeps at least 4.5:1 on the block's `bg-muted` in both.
 */

export type SqlSegmentKind =
  | "keyword"
  | "function"
  | "number"
  | "string"
  | "special"
  | "bracket"
  | "comment"
  | "identifier"
  | "whitespace";

export interface SqlSegment {
  kind: SqlSegmentKind;
  text: string;
}

const KINDS = new Set<string>([
  "keyword",
  "function",
  "number",
  "string",
  "special",
  "bracket",
  "comment",
  "identifier",
  "whitespace",
]);

/**
 * Words `sql-highlight` reads as identifiers (or, before a bracket, as functions) that are
 * keywords in the SQL dbt compiles for Snowflake and Databricks.
 */
const WAREHOUSE_KEYWORDS = new Set([
  "ASOF",
  "CLUSTER",
  "COPY",
  "CROSS",
  "CURRENT",
  "EXCEPT",
  "EXCLUDE",
  "FIRST",
  "FOLLOWING",
  "GRANT",
  "ILIKE",
  "INTERSECT",
  "LAST",
  "LATERAL",
  "MATCHED",
  "MERGE",
  "MINUS",
  "NATURAL",
  "NULLS",
  "OVER",
  "PARTITION",
  "PIVOT",
  "PRECEDING",
  "QUALIFY",
  "RANGE",
  "RECURSIVE",
  "ROW",
  "ROWS",
  "SAMPLE",
  "SCHEMA",
  "TEMP",
  "TEMPORARY",
  "TRANSIENT",
  "UNBOUNDED",
  "UNPIVOT",
  "USING",
  "WINDOW",
  "WITHIN",
]);

function kindOf(name: string, text: string): SqlSegmentKind {
  if ((name === "identifier" || name === "function") && WAREHOUSE_KEYWORDS.has(text.toUpperCase()))
    return "keyword";
  return KINDS.has(name) ? (name as SqlSegmentKind) : "special";
}

export function sqlSegments(sql: string): SqlSegment[] {
  return getSegments(sql).map(({ name, content }) => ({
    kind: kindOf(name, content),
    text: content,
  }));
}

const TOKEN_CLASS: Record<Exclude<SqlSegmentKind, "whitespace">, string> = {
  keyword: "text-info-subtle-foreground",
  function: "text-accent-text",
  number: "text-destructive-subtle-foreground",
  string: "text-success-subtle-foreground",
  special: "text-muted-foreground",
  bracket: "text-foreground-soft",
  comment: "italic text-muted-foreground",
  identifier: "text-foreground",
};

/**
 * Longer SQL is shown plain. The stored text is capped at 128 KB (`transform/skydata_dbt/node_source.py`);
 * at the cap the tokenizer takes about 150 ms and yields about 50,000 segments, redone on each open
 * (review r1). Under 64 KB it stays well below that.
 */
export const HIGHLIGHT_LIMIT = 64 * 1024;

/**
 * `SkyCodeBlock`'s `highlight`: token spans for SQL, `undefined` (plain text) for any other
 * language or for SQL longer than `HIGHLIGHT_LIMIT` characters.
 */
export function highlightCode(code: string, language?: string): ReactNode {
  if (language?.toLowerCase() !== "sql" || code.length > HIGHLIGHT_LIMIT) return undefined;
  return sqlSegments(code).map((segment, index) =>
    segment.kind === "whitespace" ? (
      segment.text
    ) : (
      <span key={index} className={TOKEN_CLASS[segment.kind]} data-sql-token={segment.kind}>
        {segment.text}
      </span>
    ),
  );
}
